import { useLazyQuery } from "@apollo/client";
import { Close, Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from "@mui/material";
import { t } from "i18next";
import React, { useState } from "react";
import { SearchPeople } from "../../api/person";
import Avatar from "../../components/Avatar";
import { Person } from "../../models/person/Person";
import { colorLightGrey, colorPrimary } from "../../rsc/colors/colors";

type Props = {
    open: boolean
    onClose: () => void
    onConfirm: (transactorId: number) => void
    loading?: boolean
}

const normalize = (value: string): string =>
    value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * search_people also matches on email, so a VIP search for "budi" can return
 * someone whose name has nothing to do with it. Only name matches are kept:
 * every word of the term has to appear in the first or last name.
 */
const matchesName = (person: any, term: string): boolean => {
    const name = normalize(`${person.first_name ?? ''} ${person.last_name ?? ''}`)
    return normalize(term).split(/\s+/).filter(Boolean).every(word => name.includes(word))
}

export const AddVipDialog: React.FC<Props> = ({ open, onClose, onConfirm, loading }) => {

    const [searchTerm, setSearchTerm] = useState("")
    /** The term the shown results were searched with, not what is being typed. */
    const [submittedTerm, setSubmittedTerm] = useState("")
    const [selected, setSelected] = useState<Person | null>(null)

    const [runSearch, { data, loading: searchLoading }] = useLazyQuery(SearchPeople, {
        fetchPolicy: "no-cache",
    })

    const handleSearch = () => {
        const term = searchTerm.trim()
        if (!term) return
        setSelected(null)
        setSubmittedTerm(term)
        runSearch({ variables: { text: term, limit: 10 } })
    }

    const handleClose = () => {
        setSearchTerm("")
        setSubmittedTerm("")
        setSelected(null)
        onClose()
    }

    const results: Person[] = (data?.result?.people ?? [])
        .filter((p: any) => p.transactor_id && matchesName(p, submittedTerm))
        .map((p: any) => Person.build(p))

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{
                bgcolor: colorPrimary(),
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 2
            }}>
                <Typography variant="h6">{t('add_vip')}</Typography>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('search_by_name')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                        InputProps={{
                            endAdornment: searchLoading ? (
                                <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                            ) : null
                        }} />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={!searchTerm.trim() || searchLoading}
                        startIcon={<Search />}>
                        {t('search')}
                    </Button>
                </Box>

                <Box sx={{ mt: 2, maxHeight: 320, overflowY: 'auto' }}>
                    {data && results.length === 0 && !searchLoading && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            {t('no_results')}
                        </Typography>
                    )}
                    <List dense>
                        {results.map((p) => {
                            const name = `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim()
                            const isSelected = selected?.id === p.id
                            return (
                                <ListItemButton
                                    key={p.id}
                                    selected={isSelected}
                                    onClick={() => setSelected(p)}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        '&.Mui-selected': { bgcolor: colorLightGrey() }
                                    }}>
                                    <ListItemAvatar>
                                        <Avatar
                                            uri={p.user?.avatar_uri}
                                            firstname={p.firstname}
                                            lastname={p.lastname}
                                            style={{ width: 40, height: 40, fontSize: '16px' }} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={name || '—'}
                                        secondary={
                                            <>
                                                {p.location?.address ? p.location.address() : null}
                                                {p.transactor?.id ? ` · ID: ${p.transactor.id}` : null}
                                            </>
                                        } />
                                </ListItemButton>
                            )
                        })}
                    </List>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} variant="outlined" color="inherit">
                    {t('close')}
                </Button>
                <LoadingButton
                    loading={loading}
                    disabled={!selected?.transactor?.id}
                    variant="contained"
                    sx={{ background: colorPrimary(), color: '#fff' }}
                    onClick={() => selected?.transactor?.id && onConfirm(selected.transactor.id)}>
                    {t('add')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default AddVipDialog;
